import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import {
  LuArrowLeft,
  LuArrowRight,
  LuLayers,
  LuLayoutGrid,
  LuMessageSquare,
  LuMinus,
  LuSquare,
  LuStar,
  LuX,
} from "react-icons/lu";
import {
  SHAPES,
  SHAPE_CATEGORIES,
  categoryLabel,
  shapeLabel,
  shapesIn,
} from "../../../../../plugins/VectorShape/catalog/shapes";
import { defaultStateFor, resolveState } from "../../../../../plugins/VectorShape/defaults";
import ShapeRenderer from "../../../../../plugins/VectorShape/components/ShapeRenderer";
import { addPluginBox } from "../elementActions";
import AssetGrid from "../ui/AssetGrid";
import AssetCell from "../ui/AssetCell";
import PanelSearch from "../ui/PanelSearch";
import "./_panel.scss";
import "../../library/_brandPanel.scss";
import "./_shapesPanel.scss";

const PLUGIN = "VectorShape";

const CATEGORY_ICONS = {
  temel: LuSquare,
  kartlar: LuLayers,
  cizgiler: LuMinus,
  oklar: LuArrowRight,
  cagri: LuMessageSquare,
  dekoratif: LuStar,
  simgeler: LuLayoutGrid,
};

const fold = (text) => (text || "").toLocaleLowerCase("tr").replace(/ı/g, "i");

// Thumbnail box: the shape's own aspect (its % size on the 16:9 canvas),
// fitted into the tile.
const THUMB_W = 60;
const THUMB_H = 40;
const thumbSize = (shape) => {
  const aspect = (parseFloat(shape.size.w) * 16) / (parseFloat(shape.size.h) * 9) || 1;
  return aspect >= THUMB_W / THUMB_H
    ? { w: THUMB_W, h: Math.max(10, THUMB_W / aspect) }
    : { w: Math.max(10, THUMB_H * aspect), h: THUMB_H };
};

// Real box width on a ~1000px canvas, to scale the corner radius down.
const REF_CANVAS_W = 1000;

const ShapeThumb = ({ shape }) => {
  const { w, h } = thumbSize(shape);
  const pxScale = Math.min(1, w / ((parseFloat(shape.size.w) / 100) * REF_CANVAS_W));
  return (
    <div className="shapes-panel__thumb">
      <div style={{ width: w, height: h }}>
        <ShapeRenderer
          state={resolveState({ shapeId: shape.id })}
          width={w}
          height={h}
          pxScale={pxScale}
          thumbnail
        />
      </div>
    </div>
  );
};

ShapeThumb.propTypes = { shape: PropTypes.object.isRequired };

const ShapesPanel = (props) => {
  const {
    boxes,
    boxSelected,
    navItems,
    navItemSelected,
    containedViews,
    containedViewSelected,
    onBoxAdded,
    onClose,
  } = props;
  const [openKey, setOpenKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const lang = i18n.language;

  const ctx = {
    boxes,
    boxSelected,
    navItems,
    navItemSelected,
    containedViews,
    containedViewSelected,
    onBoxAdded,
  };

  const term = fold(searchTerm.trim());
  const results = useMemo(
    () =>
      term
        ? SHAPES.filter((s) => Object.values(s.label).some((label) => fold(label).includes(term)))
        : [],
    [term]
  );

  const countLabel = (n) => i18n.t("ShapesPanel.count", { count: n, defaultValue: `${n} şekil` });

  const renderCell = (shape) => {
    const label = shapeLabel(shape, lang);
    const initialState = defaultStateFor(shape.id);
    return (
      <AssetCell
        key={shape.id}
        dragProps={{
          className: "palette-item floatingEditorBox",
          name: PLUGIN,
          "data-plugin": PLUGIN,
          "data-schematic": "1",
          "data-initial-state": JSON.stringify(initialState),
          "data-width": shape.size.w,
          "data-height": shape.size.h,
          "data-testid": `shape-${shape.id}`,
        }}
        ratio={0.82}
        title={label}
        label={label}
        onAdd={(e) =>
          addPluginBox(PLUGIN, e, ctx, {
            initialState,
            width: shape.size.w,
            height: shape.size.h,
          })
        }
      >
        <ShapeThumb shape={shape} />
      </AssetCell>
    );
  };

  const renderGrid = (list) => (
    <AssetGrid columns={3} cellSize={84} gap={8}>
      {list.map(renderCell)}
    </AssetGrid>
  );

  const renderCategories = () => (
    <div className="brand-panel__categories">
      {SHAPE_CATEGORIES.map((category) => {
        const Icon = CATEGORY_ICONS[category.key];
        return (
          <button
            key={category.key}
            type="button"
            className="brand-panel__category"
            data-testid={`shape-category-${category.key}`}
            onClick={() => setOpenKey(category.key)}
          >
            <span className="brand-panel__category-icon">
              <Icon size={17} />
            </span>
            <span className="brand-panel__category-text">
              <span className="brand-panel__category-name">{categoryLabel(category, lang)}</span>
              <span className="brand-panel__category-count">
                {countLabel(shapesIn(category.key).length)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderCategory = (category) => (
    <>
      <div className="brand-panel__category-head">
        <button
          type="button"
          className="brand-panel__back"
          data-testid="shapes-back"
          title={i18n.t("ShapesPanel.back", { defaultValue: "Geri" })}
          onClick={() => setOpenKey(null)}
        >
          <LuArrowLeft size={14} />
        </button>
        <span className="brand-panel__title">{categoryLabel(category, lang)}</span>
        <span className="shapes-panel__count">{countLabel(shapesIn(category.key).length)}</span>
      </div>
      {renderGrid(shapesIn(category.key))}
    </>
  );

  const renderBody = () => {
    if (term) {
      return results.length ? (
        renderGrid(results)
      ) : (
        <div className="shapes-panel__empty">
          {i18n.t("ShapesPanel.empty", { defaultValue: "Aramanla eşleşen şekil yok" })}
        </div>
      );
    }
    const open = SHAPE_CATEGORIES.find((c) => c.key === openKey);
    return open ? renderCategory(open) : renderCategories();
  };

  return (
    <div className="cmp-panel shapes-panel">
      <div className="cmp-panel__toolbar">
        <PanelSearch
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm("")}
          placeholder={i18n.t("search") || "Ara"}
        />
        {onClose && (
          <button
            type="button"
            className="cmp-panel__close"
            onClick={onClose}
            title={i18n.t("close") || "Kapat"}
          >
            <LuX size={15} />
          </button>
        )}
      </div>
      <div className="cmp-panel__scroll">{renderBody()}</div>
    </div>
  );
};

ShapesPanel.propTypes = {
  boxes: PropTypes.object.isRequired,
  boxSelected: PropTypes.any,
  navItems: PropTypes.object.isRequired,
  navItemSelected: PropTypes.any.isRequired,
  containedViews: PropTypes.object.isRequired,
  containedViewSelected: PropTypes.any,
  onBoxAdded: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default ShapesPanel;
